import { Mail } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useConsumeMagicLink, useRequestMagicLink } from "@/api/hooks";

/** Lands when the user clicks a magic-link email.
 *
 * Two flows depending on platform:
 *
 * - **Desktop / non-iOS**: auto-consume the token (POST consume,
 *   cookie set), then redirect to /. The user is logged into the
 *   web admin on this browser.
 * - **iOS Safari**: don't auto-consume. Show two buttons —
 *   "Open in PencilOut app" (deep-links the token into the iOS app
 *   via `pencilout://auth/consume?token=…`) and "Sign in to web
 *   admin" (auto-consume here). The iOS app will be the more common
 *   target since field users live on their phones.
 *
 * If both web and app try to consume the same token, the second
 * attempt gets 410 Gone — magic links are single-use. The UI on
 * either side handles that gracefully. */
export function MagicLinkConsumePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  // ``returnTo`` is set by the iOS auth-handoff flow (and any future
  // app-to-browser handoffs). When present we navigate there after
  // consume instead of falling back to the pending-plan crumb. URL-
  // decoded automatically by useSearchParams.
  const returnTo = searchParams.get("returnTo");
  // ``from=ios`` flags this consume as the back-half of an iOS handoff.
  // Two effects:
  //   1. Skip the iOS chooser (the user already decided to continue
  //      on web — they just tapped the iOS upgrade button); auto-
  //      consume directly.
  //   2. Persist a session-storage flag so the post-Stripe-checkout
  //      success page can offer a "Return to PencilOut" CTA back
  //      into the iOS app via ``pencilout://billing/upgraded``.
  const fromIOS = searchParams.get("from") === "ios";
  const navigate = useNavigate();
  const consume = useConsumeMagicLink();
  const isIOS = useMemo_isIOS();
  const [chose, setChose] = useState<"app" | "web" | null>(null);

  // After auth lands, route to ``returnTo`` if the iOS handoff set
  // it, else /billing?plan=<tier> if signup left a pending-plan
  // crumb (?plan=pro/crew on the marketing CTA), else /.
  // Read+clear in one shot so re-renders don't double-consume.
  function postConsumeNavigate() {
    if (returnTo) {
      // Sanity check — only allow same-origin paths to avoid an
      // open-redirect via the consume URL. Anything that doesn't
      // start with "/" gets ignored.
      if (returnTo.startsWith("/")) {
        navigate(returnTo, { replace: true });
        return;
      }
    }
    let target = "/";
    try {
      const pending = window.localStorage.getItem("pencilout_pending_plan");
      if (pending && (pending === "pro" || pending === "crew")) {
        target = `/billing?plan=${pending}`;
      }
      window.localStorage.removeItem("pencilout_pending_plan");
    } catch {
      /* localStorage unavailable; fall back to / */
    }
    navigate(target, { replace: true });
  }

  // Persist the "came from iOS app" flag so the upgrade-success
  // page can offer a return-to-app CTA. Cleared by the consumer
  // (PlanBillingPage) once acted on. SessionStorage (not local) so
  // it doesn't outlive the tab.
  useEffect(() => {
    if (!fromIOS) return;
    try {
      window.sessionStorage.setItem("pencilout_return_to_app", "1");
    } catch {
      /* sessionStorage unavailable; non-fatal */
    }
  }, [fromIOS]);

  // Auto-consume on non-iOS platforms, OR on iOS when the URL
  // explicitly says ``from=ios`` (the user already chose web by
  // tapping Upgrade in the iOS app — don't re-prompt them).
  // biome-ignore lint/correctness/useExhaustiveDependencies: fire-once-on-mount; consume.mutate identity changes per render and postConsumeNavigate closes over navigate (already covered transitively); adding them would re-fire the consume mid-redirect. `chose` only flows null→value, and the early-return covers it.
  useEffect(() => {
    if (!token || chose) return;
    if (isIOS && !fromIOS) return;
    consume.mutate(
      { token },
      {
        onSuccess: postConsumeNavigate,
      },
    );
  }, [token, isIOS, fromIOS]);

  // Manual consume when the user picks "Sign in to web admin" on iOS.
  // biome-ignore lint/correctness/useExhaustiveDependencies: fires when user chooses "web"; consume.mutate identity churn would re-fire on every render of this page. postConsumeNavigate is stable enough — it only reads navigate which is itself stable from react-router.
  useEffect(() => {
    if (!token || chose !== "web") return;
    consume.mutate(
      { token },
      {
        onSuccess: postConsumeNavigate,
      },
    );
  }, [chose, token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6 text-graphite">
      <div className="w-full max-w-md text-center">
        <div className="mb-6">
          <span className="brand-wordmark text-3xl">PencilOut</span>
          <span className="label-mono ml-2">admin</span>
        </div>

        {!token && (
          <ResendForm
            heading="Missing sign-in token"
            body="No token in the URL — the link may have been cut off by your email client. Type your email and we'll send a fresh one."
          />
        )}

        {/* iOS chooser */}
        {token && isIOS && !chose && (
          <div className="rounded-lg border border-rule bg-paper-warm p-6 text-left">
            <h1 className="font-mono text-lg font-bold tracking-tight">Sign in</h1>
            <p className="mt-2 text-sm text-graphite-soft">
              Pick where you want to sign in. The link is single-use, so the option you don't pick
              won't work afterward.
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <a
                href={`pencilout://auth/consume?token=${encodeURIComponent(token)}`}
                onClick={() => setChose("app")}
                className="block rounded bg-graphite px-3 py-2 text-center font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft"
              >
                Open in PencilOut app
              </a>
              <button
                type="button"
                onClick={() => setChose("web")}
                className="rounded border border-rule bg-paper px-3 py-2 font-mono text-sm font-semibold text-graphite transition hover:bg-pencil-yellow-soft"
              >
                Sign in to web admin instead
              </button>
            </div>
            <p className="mt-3 font-mono text-[10px] text-graphite-faint">
              No PencilOut app yet? Hit "web admin" — the iOS app is invite-only for now.
            </p>
          </div>
        )}

        {/* Auto-consume in flight */}
        {token && (consume.isIdle || consume.isPending) && !isIOS && (
          <p className="text-sm text-graphite-soft">Signing in…</p>
        )}
        {token && consume.isPending && chose === "web" && (
          <p className="text-sm text-graphite-soft">Signing in…</p>
        )}

        {token && consume.isError && <ConsumeErrorCard error={consume.error} />}
      </div>
    </div>
  );
}

/** Render the consume failure. For ``expired`` / ``consumed`` (which
 *  the API marks via ``ApiError.errorCode``), inline a fresh-link
 *  request form so the user doesn't have to navigate away. Other
 *  failures fall back to a generic card with a /login link. */
function ConsumeErrorCard({ error }: { error: unknown }) {
  const apiErr = error instanceof ApiError ? error : null;
  const code = apiErr?.errorCode ?? null;
  const message =
    apiErr?.detail ?? "We couldn't redeem that link. It may be expired or already used.";

  if (code === "expired" || code === "consumed") {
    const heading = code === "expired" ? "Sign-in link expired" : "Sign-in link already used";
    const body =
      code === "expired"
        ? "Magic links expire 15 minutes after they're sent. Type your email below and we'll send a fresh one."
        : "Each sign-in link is good for one use. Type your email below and we'll send a fresh one.";
    return <ResendForm heading={heading} body={body} />;
  }

  return (
    <div className="rounded-lg border border-rule bg-paper-warm p-6">
      <h1 className="font-mono text-lg font-bold tracking-tight">Sign-in failed</h1>
      <p className="mt-2 text-sm text-graphite-soft">{message}</p>
      <Link
        to="/login"
        className="mt-4 inline-block rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft"
      >
        Request a new link
      </Link>
    </div>
  );
}

function ResendForm({ heading, body }: { heading: string; body: string }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const request = useRequestMagicLink();

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      await request.mutateAsync({ email: email.trim() });
      setSent(true);
    } catch {
      /* surfaces below via request.error */
    }
  }

  if (sent) {
    return (
      <div className="rounded-lg border border-rule bg-paper-warm p-6 text-center">
        <Mail className="mx-auto h-6 w-6 text-pencil-yellow" aria-hidden />
        <h1 className="mt-3 font-mono text-lg font-bold tracking-tight">Check your inbox</h1>
        <p className="mt-2 text-sm text-graphite-soft">
          A fresh sign-in link is on its way to{" "}
          <span className="font-mono text-graphite">{email.trim()}</span>. The link is good for 15
          minutes.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-4 rounded-lg border border-rule bg-paper-warm p-6 text-left"
    >
      <div>
        <h1 className="font-mono text-lg font-bold tracking-tight">{heading}</h1>
        <p className="mt-2 text-sm text-graphite-soft">{body}</p>
      </div>

      <label className="block">
        <span className="label-mono">Email</span>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="brad@pencilout.app"
          className="mt-1 w-full rounded border border-rule bg-paper px-3 py-2 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
        />
      </label>

      {request.error && (
        <p className="text-sm text-red-pencil">
          {request.error instanceof ApiError
            ? request.error.detail
            : "Could not send sign-in email."}
        </p>
      )}

      <button
        type="submit"
        disabled={request.isPending || !email.trim()}
        className="w-full rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
      >
        {request.isPending ? "Sending…" : "Send me a fresh sign-in link"}
      </button>
    </form>
  );
}

/** Tiny iOS detection — UA-sniff is good enough for "should we offer
 * the app?". iPad on iPadOS 13+ identifies as Mac, so we also check
 * touch + maxTouchPoints to catch that case. */
function useMemo_isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/iPhone|iPod/.test(ua)) return true;
  if (/iPad/.test(ua)) return true;
  // iPadOS-as-Mac fallback.
  if (
    /Macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1
  ) {
    return true;
  }
  return false;
}
